import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, CreditCard, BookOpen, Zap, Star } from 'lucide-react';
import { CTAButton } from './Shared';
import { FeatherQuill, Flame, MoonPhase, LotusBud } from './HealingIcons';
export const Hero = () => {
  return <div className="relative overflow-hidden pb-8 md:pt-20 md:pb-12 bg-transparent pt-[10px]" data-id="element-128">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-aquaLight/10 rounded-full blur-[140px] -z-10 pointer-events-none" data-id="element-129"></div>

      <FeatherQuill className="hidden md:block absolute top-32 left-[6%] w-8 h-8 opacity-30 animate-float-soft -z-10" color="#0D9488" style={{
      transform: 'rotate(20deg)'
    }} data-id="element-130" />
      <MoonPhase className="hidden md:block absolute top-48 right-[8%] w-10 h-10 opacity-30 animate-float-soft -z-10" color="#5EEAD4" style={{
      transform: 'rotate(-25deg)',
      animationDelay: '1.2s'
    }} data-id="element-131" />
      <LotusBud className="hidden md:block absolute bottom-40 left-[4%] w-12 h-12 opacity-40 animate-float-soft -z-10" color="#D4AF37" style={{
      animationDelay: '2s'
    }} data-id="element-132" />
      <Flame className="hidden md:block absolute bottom-32 right-[5%] w-8 h-8 opacity-50 animate-float-soft animate-ember -z-10" color="#A07C1E" style={{
      transform: 'rotate(15deg)',
      animationDelay: '0.6s'
    }} data-id="element-133" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-134">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="bg-transparent space-y-[0px]" data-id="element-135">
          <div className="inline-flex items-center justify-center bg-white/80 backdrop-blur-sm border border-brand-aqua/30 px-4 py-1.5 rounded-full mb-6 gap-2 shadow-sm pt-[2px] pb-[2px] pl-[10px] pr-[10px]" data-id="element-136">
            <span className="text-sm" data-id="element-137">🌿</span>
            <span className="font-serif font-semibold tracking-wider text-xs md:text-sm text-brand-darkGreen" data-id="element-138">
              LUMEN AKASHA™
            </span>
          </div>

          <div className="relative inline-block mb-4 max-w-4xl mx-auto" data-id="element-139">
            <div className="absolute inset-0 -z-10 blur-3xl opacity-60 pointer-events-none" data-id="element-140">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[110%] h-[140%] bg-gradient-to-r from-brand-aquaLight/30 via-brand-gold/20 to-brand-aqua/30 rounded-full" data-id="element-141" />
            </div>

            <span className="sparkle absolute -top-2 left-[8%] text-brand-aqua text-sm md:text-xl" style={{
            animationDelay: '0s'
          }} data-id="element-142">
              ✦
            </span>
            <span className="sparkle absolute top-1 right-[6%] text-brand-gold text-xs md:text-lg" style={{
            animationDelay: '0.8s'
          }} data-id="element-143">
              ✧
            </span>
            <span className="sparkle absolute -bottom-1 left-[20%] text-brand-aquaLight text-xs md:text-base" style={{
            animationDelay: '1.6s'
          }} data-id="element-144">
              ✦
            </span>
            <span className="sparkle absolute bottom-2 right-[18%] text-brand-aqua text-sm md:text-lg" style={{
            animationDelay: '2.2s'
          }} data-id="element-145">
              ✧
            </span>

            <h1 className="sm:text-4xl md:text-6xl lg:text-7xl text-[28px] font-[700] leading-tight tracking-tight" style={{
            fontFamily: "'Cinzel', serif"
          }} data-id="element-146">
              <span className="headline-shine text-[26px]" data-id="element-147">
                NO TE FALTAN CONOCIMIENTOS.
              </span>
              <br data-id="element-148" />
              <span className="phrase-glow text-[24px] font-[700] font-[Cinzel,_serif] text-[#0F766E] bg-[#022C22]" data-id="element-149">
                TE FALTA VISIBILIDAD.
              </span>
              <span className="text-brand-darkGreen" data-id="element-150">.</span>
            </h1>
          </div>

          <div className="relative z-10 w-full max-w-[280px] sm:max-w-sm md:max-w-lg lg:max-w-xl xl:max-w-2xl mx-auto mb-2 flex justify-center" data-id="element-151">
            <div className="relative overflow-hidden rounded-2xl bg-transparent w-full mx-auto" style={{
            aspectRatio: '1/1',
            maxHeight: '500px'
          }} data-id="element-152">
              <motion.img src="https://cdn.magicpatterns.com/uploads/dQB66AQw87eyNxk3YHmfNh/MOCKUP.png" alt="Kit Digital para Terapeutas" initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              ease: 'easeOut'
            }} className="absolute inset-0 w-full h-full object-contain mx-auto drop-shadow-2xl" data-id="element-153" />
            </div>
          </div>

          <div className="max-w-3xl mx-auto mb-8 mt-8 space-y-3 px-2" data-id="element-154">
            <p className="text-[11px] md:text-base lg:text-lg text-brand-text font-sans" data-id="element-155">
              No necesitás más conocimientos. Necesitás que las personas
              correctas descubran los que ya tenés. Aprendé a mostrar tu trabajo
              y atraé más consultas.
            </p>
          </div>

          <div className="flex flex-nowrap items-center justify-center gap-1.5 md:gap-3 mb-12 text-xs md:text-sm font-medium px-2" data-id="element-156">
            <div className="inline-flex items-center bg-white px-2 md:px-3 py-1.5 md:py-2 rounded-full border border-brand-aqua/25 whitespace-nowrap shadow-sm text-[9px] md:text-sm" data-id="element-157">
              <BookOpen className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-1.5 text-brand-aqua shrink-0" data-id="element-158" />
              100% PDF
            </div>
            <div className="inline-flex items-center bg-white px-2 md:px-3 py-1.5 md:py-2 rounded-full border border-brand-aqua/25 whitespace-nowrap shadow-sm text-[9px] md:text-sm" data-id="element-159">
              <Zap className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-1.5 text-brand-aqua shrink-0" data-id="element-160" />
              Acceso en 5 min
            </div>
            <div className="inline-flex items-center bg-white px-2 md:px-3 py-1.5 md:py-2 rounded-full border border-brand-aqua/25 whitespace-nowrap shadow-sm text-[9px] md:text-sm" data-id="element-161">
              <span className="mr-1 md:mr-1.5 text-brand-aqua shrink-0" data-id="element-162">
                🌿
              </span>
              Cualquier terapia
            </div>
          </div>

          <div className="flex flex-col items-center space-y-6" data-id="element-163">
            <CTAButton variant="green" href="#pricing" className="text-base md:text-xl px-6 md:px-10 py-4 md:py-5 cta-shimmer relative overflow-hidden" data-id="element-164">
              <span className="relative z-10" data-id="element-165">
                → QUIERO MI KIT DIGITAL AHORA
              </span>
            </CTAButton>

            <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2 text-xs md:text-sm text-brand-muted" data-id="element-166">
              <div className="flex items-center" data-id="element-167">
                <Shield className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-aqua" data-id="element-168" />{' '}
                Garantía 7 días
              </div>
              <div className="flex items-center" data-id="element-169">📥 Descarga inmediata</div>
              <div className="flex items-center" data-id="element-170">
                🖨️ Listo para aplicar desde hoy
              </div>
              <div className="flex items-center" data-id="element-171">
                <Lock className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-aqua" data-id="element-172" />{' '}
                Pago seguro
              </div>
              <div className="flex items-center" data-id="element-173">
                <CreditCard className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-aqua" data-id="element-174" />{' '}
                Mercado Pago
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="mt-12 border-y border-brand-aqua/15 bg-white/60 py-4 backdrop-blur-sm pt-[10px] pb-[10px]" data-id="element-175">
        <div className="md:hidden flex flex-wrap justify-center gap-x-5 gap-y-2 px-4 text-sm pl-[16px] pr-[16px]" data-id="element-176">
          <div className="flex items-center" data-id="element-177">
            <span className="font-bold text-brand-aqua mr-1.5" data-id="element-178">+200</span>{' '}
            terapeutas equipados
          </div>
          <div className="flex items-center" data-id="element-179">
            <span className="font-bold text-brand-aqua mr-1.5" data-id="element-180">1</span> Sistema
            principal
          </div>
          <div className="flex items-center" data-id="element-181">
            <span className="font-bold text-brand-aqua mr-1.5" data-id="element-182">3</span> Bonus
            incluidos
          </div>
          <div className="flex items-center" data-id="element-183">
            <Star className="w-3.5 h-3.5 text-brand-gold fill-brand-gold mr-1" data-id="element-184" />{' '}
            Material aplicable
          </div>
        </div>

        <div className="hidden md:flex flex-row items-center gap-6 justify-center whitespace-nowrap px-4 text-sm lg:text-base" data-id="element-185">
          <div className="flex items-center" data-id="element-186">
            <span className="font-bold text-brand-aqua mr-2" data-id="element-187">+200</span>{' '}
            terapeutas equipados
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-aquaLight/50 shrink-0" data-id="element-188"></div>
          <div className="flex items-center" data-id="element-189">
            <span className="font-bold text-brand-aqua mr-2" data-id="element-190">1</span> Sistema
            principal
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-aquaLight/50 shrink-0" data-id="element-191"></div>
          <div className="flex items-center" data-id="element-192">
            <span className="font-bold text-brand-aqua mr-2" data-id="element-193">3</span> Bonus
            incluidos
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-aquaLight/50 shrink-0" data-id="element-194"></div>
          <div className="flex items-center" data-id="element-195">
            <Star className="w-4 h-4 text-brand-gold fill-brand-gold mr-1" data-id="element-196" />{' '}
            Material aplicable
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 md:mt-16 mb-4" data-id="element-197">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left" data-id="element-198">
          <div className="glass-panel p-6 rounded-2xl border-brand-aqua/20 bg-white/80 shadow-sm flex flex-col" data-id="element-199">
            <div className="flex text-brand-gold mb-3" data-id="element-200">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" data-id="element-201" />)}
            </div>
            <p className="text-brand-text/90 italic mb-6 text-sm md:text-base font-serif leading-relaxed flex-grow" data-id="element-202">
              "Antes no sabía qué publicar y mis redes estaban abandonadas. Con
              el kit, en una semana organicé todo mi mes y ya tuve 3 consultas
              nuevas. ¡Súper práctico!"
            </p>
            <div className="flex items-center gap-3 mt-auto" data-id="element-203">
              <div className="w-10 h-10 rounded-full bg-brand-aqua/10 flex items-center justify-center text-brand-darkGreen font-bold font-serif border border-brand-aqua/20" data-id="element-204">
                LM
              </div>
              <div data-id="element-205">
                <div className="font-medium text-brand-darkGreen text-sm" data-id="element-206">
                  Laura M.
                </div>
                <div className="text-xs text-brand-muted" data-id="element-207">
                  Terapeuta Holística
                </div>
              </div>
            </div>
          </div>

          <div className="glass-panel p-6 rounded-2xl border-brand-aqua/20 bg-white/80 shadow-sm flex flex-col" data-id="element-208">
            <div className="flex text-brand-gold mb-3" data-id="element-209">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" data-id="element-210" />)}
            </div>
            <p className="text-brand-text/90 italic mb-6 text-sm md:text-base font-serif leading-relaxed flex-grow" data-id="element-211">
              "Las plantillas de WhatsApp me salvaron. Ahora respondo de forma
              profesional y noto que la gente confía mucho más antes de la
              primera sesión."
            </p>
            <div className="flex items-center gap-3 mt-auto" data-id="element-212">
              <div className="w-10 h-10 rounded-full bg-brand-aqua/10 flex items-center justify-center text-brand-darkGreen font-bold font-serif border border-brand-aqua/20" data-id="element-213">
                MS
              </div>
              <div data-id="element-214">
                <div className="font-medium text-brand-darkGreen text-sm" data-id="element-215">
                  Martín S.
                </div>
                <div className="text-xs text-brand-muted" data-id="element-216">
                  Facilitador Akáshico
                </div>
              </div>
            </div>
          </div>

          <div className="glass-panel p-6 rounded-2xl border-brand-aqua/20 bg-white/80 shadow-sm flex flex-col" data-id="element-217">
            <div className="flex text-brand-gold mb-3" data-id="element-218">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" data-id="element-219" />)}
            </div>
            <p className="text-brand-text/90 italic mb-6 text-sm md:text-base font-serif leading-relaxed flex-grow" data-id="element-220">
              "Me daba terror mostrarme en redes. El manual me dio la estructura
              y la seguridad que necesitaba para comunicar mi valor sin sentir
              que estaba 'vendiendo'."
            </p>
            <div className="flex items-center gap-3 mt-auto" data-id="element-221">
              <div className="w-10 h-10 rounded-full bg-brand-aqua/10 flex items-center justify-center text-brand-darkGreen font-bold font-serif border border-brand-aqua/20" data-id="element-222">
                CP
              </div>
              <div data-id="element-223">
                <div className="font-medium text-brand-darkGreen text-sm" data-id="element-224">
                  Carolina P.
                </div>
                <div className="text-xs text-brand-muted" data-id="element-225">Terapeuta Floral</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};